import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  message,
  Select,
  Divider,
  Modal,
  Dropdown,
  Menu,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  EllipsisOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  useCreateEvaluationForm,
  useEvaluationForms,
  useEvaluationFormWithSections,
  useDeleteEvaluationForm,
} from "./logic/useEvaluationForm";
import {
  useUpdateEvaluationSection,
  useDeleteEvaluationSection,
} from "../EvaluationSectionForm/logic/useEvaluationSection";
import EvaluationSectionForm from "../EvaluationSectionForm/EvaluationSectionForm";
import EvaluationQuestionForm from "../EvaluationQuestionForm/EvaluationQuestionForm";
import EditableQuestion from "../EvaluationQuestionForm/components/EditableQuestion";
import UpdateSectionModal from "../EvaluationSectionForm/EditSectionModal";
import { useGroupedQuestions } from "../EvaluationQuestionForm/logic/useEvaluationQuestion";
import "./EvaluationFormHeader.scss";
import EditFormComponent from "./EditFormComponent";
import FormViewer from "./components/FormViewer";
import { EyeOutlined } from "@ant-design/icons";
import CollectEvaluationResponses from "./components/CollectEvaluationResponses";

const { Option } = Select;

const EvaluationFormHeader = () => {
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState(null);
  const [isEditingForm, setIsEditingForm] = useState(false);
  const [editFormValues, setEditFormValues] = useState(null);
  const { mutate: deleteForm } = useDeleteEvaluationForm();
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const {
    data: forms = [],
    isLoading: loadingForms,
    refetch: refetchForms,
  } = useEvaluationForms();
  const { data: formDetails, refetch: refetchSections } =
    useEvaluationFormWithSections(selectedFormId);
  const { data: groupedQuestions = {}, refetch: refetchQuestions } =
    useGroupedQuestions(selectedFormId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { mutate: createForm, isLoading: creatingForm } =
    useCreateEvaluationForm();
  const { mutate: updateSection } = useUpdateEvaluationSection();
  const { mutate: deleteSection } = useDeleteEvaluationSection();

  const onSubmit = (data) => {
    createForm(
      { ...data, year: Number(data.year) },
      {
        onSuccess: (form) => {
          message.success("Form created successfully!");
          reset();
          refetchForms();
          setSelectedFormId(form.id);
        },
        onError: (err) => {
          message.error(
            err?.response?.data?.message || "Failed to create form"
          );
        },
      }
    );
  };
  const handleDeleteForm = (formId) => {
    deleteForm(formId, {
      onSuccess: () => {
        message.success("Form deleted successfully");
        setSelectedFormId(null);
        refetchForms();
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Delete failed");
      },
    });
  };

  const handleSectionAdded = () => {
    refetchSections();
    refetchQuestions();
  };

  const handleEditSection = (section) => {
    setSectionToEdit(section);
    setIsEditModalOpen(true);
  };

  const handleUpdate = (id, values) => {
    updateSection(
      { id, data: values },
      {
        onSuccess: () => {
          message.success("Section updated successfully!");
          setIsEditModalOpen(false);
          handleSectionAdded();
        },
        onError: (err) => {
          message.error(err?.response?.data?.message || "Update failed");
        },
      }
    );
  };

  const handleDeleteSection = (sectionId) => {
    deleteSection(sectionId, {
      onSuccess: () => {
        message.success("Section deleted successfully");
        handleSectionAdded();
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Delete failed");
      },
    });
  };

  return (
    <div className="form-header-container">
      <div className="form-banner">
        <h1>AVAXIA Skills Assessment</h1>
        <p className="form-date">{new Date().toLocaleDateString()}</p>
        <p className="form-description">
          Skills collection form for employees working in AVAXIA
        </p>
      </div>

      <div className="form-block">
        <label className="form-label">Select Existing Form</label>
        <Select
          loading={loadingForms}
          className="form-select"
          placeholder="Select a form"
          onChange={setSelectedFormId}
          value={selectedFormId || undefined}
        >
          {forms.map((form) => (
            <Option key={form.id} value={form.id}>
              {form.title} ({form.year})
            </Option>
          ))}
        </Select>
      </div>

      <Divider className="divider" plain>
        OR
      </Divider>

      <form onSubmit={handleSubmit(onSubmit)} className="form-creator">
        <div className="form-field">
          <label>Title</label>
          <input {...register("title", { required: "Title is required" })} />
          {errors.title && (
            <span className="error">{errors.title.message}</span>
          )}
        </div>

        <div className="form-field">
          <label>Year</label>
          <input
            type="number"
            {...register("year", { required: "Year is required", min: 2000 })}
          />
          {errors.year && <span className="error">{errors.year.message}</span>}
        </div>

        <div className="form-field col-span">
          <label>Description</label>
          <textarea
            rows={3}
            {...register("description", {
              required: "Description is required",
            })}
          />
          {errors.description && (
            <span className="error">{errors.description.message}</span>
          )}
        </div>

        <div className="top-actions col-span">
          <button disabled={creatingForm} type="submit" className="add-btn">
            {creatingForm ? "Creating..." : "Create New Form"}
          </button>
        </div>
      </form>

      {selectedFormId && formDetails && (
        <div className="form-sections">
          <div className="form-header-details">
            <div className="form-header-bar">
              <h3>
                {formDetails.title}{" "}
                <span className="form-year">({formDetails.year})</span>
              </h3>

              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      onClick={() => {
                        setEditFormValues(formDetails);
                        setIsEditingForm(true);
                      }}
                    >
                      Edit Form
                    </Menu.Item>
                    <Menu.Item danger>
                      <Popconfirm
                        title="Are you sure you want to delete this form?"
                        onConfirm={() => handleDeleteForm(selectedFormId)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Tooltip title="Delete Form">
                          <span style={{ display: "block" }}>Delete</span>
                        </Tooltip>
                      </Popconfirm>
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <EllipsisOutlined className="form-menu-icon" />
              </Dropdown>
            </div>

            <p>{formDetails.description}</p>

            {isEditingForm && editFormValues && (
              <EditFormComponent
                form={editFormValues}
                onClose={() => setIsEditingForm(false)}
                onUpdated={() => {
                  refetchForms();
                  refetchSections();
                  setIsEditingForm(false);
                }}
              />
            )}
          </div>

          <div
            className="top-actions"
            style={{ textAlign: "right", marginBottom: "1.5rem" }}
          >
            <button className="add-btn" onClick={() => setIsModalOpen(true)}>
              <PlusOutlined style={{ marginRight: 8 }} />
              Add Section
            </button>
            <button className="view-btn" onClick={() => setShowViewModal(true)}>
              <EyeOutlined style={{ marginRight: 8 }} />
              View Form
            </button>
            <button className="view-btn" onClick={() => setShowResponses(true)}>
              <FileSearchOutlined style={{ marginRight: 8 }} /> Collect
              Responses
            </button>
          </div>
          <Modal
            open={showViewModal}
            onCancel={() => setShowViewModal(false)}
            footer={null}
            width="70%"
            style={{ top: 20 }}
            className="view-form-modal"
          >
            {formDetails?.sections?.length > 0 ? (
              <FormViewer
                formDetails={formDetails}
                groupedQuestions={groupedQuestions}
              />
            ) : (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                Loading form...
              </div>
            )}
          </Modal>

          <Modal
            title="Add New Section"
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={null}
          >
            <EvaluationSectionForm
              formId={selectedFormId}
              onSuccess={() => {
                setIsModalOpen(false);
                handleSectionAdded();
              }}
            />
          </Modal>

          <UpdateSectionModal
            open={isEditModalOpen}
            onCancel={() => setIsEditModalOpen(false)}
            onUpdate={handleUpdate}
            section={sectionToEdit}
          />
          {showResponses && (
            <CollectEvaluationResponses
              formId={formDetails.id}
              onClose={() => setShowResponses(false)}
            />
          )}

          {formDetails.sections?.map((section) => {
            const sectionMenu = (
              <Menu>
                <Menu.Item onClick={() => handleEditSection(section)}>
                  Edit
                </Menu.Item>
                <Menu.Item danger>
                  <Popconfirm
                    title="Are you sure you want to delete this section?"
                    onConfirm={() => handleDeleteSection(section.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Delete Section">
                      <span style={{ display: "block" }}>Delete</span>
                    </Tooltip>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            );

            return (
              <div key={section.id} className="styled-section-card">
                <div className="section-header-wrapper">
                  <div className="section-header-bar">
                    <div className="section-order-label">
                      Section {section.order}
                    </div>
                    <Dropdown overlay={sectionMenu} trigger={["click"]}>
                      <EllipsisOutlined className="section-menu-icon" />
                    </Dropdown>
                  </div>
                  <h2 className="section-main-title">
                    Section {section.order}: {section.title}
                  </h2>
                </div>
                {groupedQuestions?.[section.title]
                  ?.slice()
                  ?.sort((a, b) => a.order - b.order)
                  ?.map((q, index) => (
                    <EditableQuestion
                      key={q.id}
                      question={{ ...q, formId: selectedFormId }}
                      index={q.order}
                      onUpdated={handleSectionAdded}
                    />
                  ))}

                <EvaluationQuestionForm
                  sectionId={section.id}
                  formId={selectedFormId}
                  onSuccess={handleSectionAdded}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EvaluationFormHeader;
